import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import {
	Title,
	TextInput,
	Button,
	Group,
	Table,
	Text,
	Paper,
	Box,
	Space,
	MediaQuery,
} from '@mantine/core'
import CameraScanner from '../components/CameraScanner'
import { searchCheckIns } from '../services/actions/checkInActions'

const ExperienceSearchPage = () => {
	const { experienceSlug } = useParams()
	const navigate = useNavigate()

	const [query, setQuery] = useState('')
	const [checkIns, setCheckIns] = useState([])
	const [error, setError] = useState('')
	const [scanning, setScanning] = useState(false)
	const [hasSearched, setHasSearched] = useState(false)

	const currentExperience = useSelector(
		(state) => state.experience?.experienceDetails
	)
	const userStation = useSelector(
		(state) => state.experience?.userPosition?.station
	)
	const userStationName = useSelector(
		(state) => state.experience?.userPosition?.stationName
	)

	if (!currentExperience?.id) {
		navigate('/')
		return null
	}

	const handleSearch = async (e) => {
		e.preventDefault()
		setError('')

		if (query.trim().length < 3) {
			setError('Please enter at least 3 characters.')
			setCheckIns([])
			setHasSearched(false)
			return
		}

		try {
			const results = await searchCheckIns(experienceSlug, query)
			setCheckIns(results)
			setHasSearched(true)
		} catch (err) {
			console.error(err)
			setError('Search failed. Please try again.')
		}
	}

	const handleSelect = (checkIn) => {
		navigate(`/experiences/${experienceSlug}/checkIn/${checkIn.id}`)
	}

	const handleScanSuccess = async (data) => {
		setScanning(false)
		setError('')
		try {
			const results = await searchCheckIns(experienceSlug, data)
			setCheckIns(results)
			setHasSearched(true)
			if (results.length === 1) {
				navigate(`/experiences/${experienceSlug}/checkIn/${results[0].id}`)
			} else {
				setQuery(data)
				setError(results.length === 0 ? 'No results found.' : '')
			}
		} catch (err) {
			console.error(err)
			setError('Scan search failed.')
		}
	}

	const handleInputChange = async (e) => {
		const value = e.target.value
		setQuery(value)

		if (value.trim().length < 3) {
			setCheckIns([])
			setHasSearched(false)
			setError('')
			return
		}

		try {
			const results = await searchCheckIns(experienceSlug, value)
			setCheckIns(results)
			setHasSearched(true)
			setError(results.length === 0 ? 'No results found.' : '')
		} catch (err) {
			console.error(err)
			setError('Auto-search failed.')
		}
	}

	const alreadyCheckedIn = checkIns.filter((c) => c.checkInStatus === true)
	const activeCheckIns = checkIns.filter((c) => !c.checkInStatus)
	const myStationCheckIns = activeCheckIns.filter(
		(c) => c.station === userStation
	)
	const otherStationCheckIns = activeCheckIns.filter(
		(c) => c.station !== userStation
	)

	const renderCards = (data, statusText) =>
		data.map((b) => (
			<Paper
				withBorder
				p='sm'
				radius='md'
				mb='sm'
				key={b.id}
				style={statusText ? { opacity: 0.7 } : {}}
			>
				<Group position='apart' align='flex-start'>
					<Box>
						<Text fw={600}>{b.travelerName}</Text>
						<Text size='xs' c='dimmed'>
							{b.travelerEmail}
						</Text>
						<Text size='xs' c='dimmed'>
							{b.travelerPhone}
						</Text>
						<Text size='xs'>Date: {b.scheduledCheckInDate?.slice(0, 10)}</Text>
						<Text size='xs'>Station: {b.stationName}</Text>
						{statusText && (
							<Text size='xs' c='green'>
								{statusText}
							</Text>
						)}
					</Box>
					<Button
						size='xs'
						variant={statusText ? 'light' : 'filled'}
						onClick={() => handleSelect(b)}
					>
						{statusText ? 'View' : 'Select'}
					</Button>
				</Group>
			</Paper>
		))

	const renderTable = (data, showStatus) => (
		<Table striped highlightOnHover>
			<thead>
				<tr>
					<th>Name</th>
					<th>Email</th>
					<th>Phone</th>
					<th>Date</th>
					<th>Station</th>
					{showStatus && <th>Status</th>}
					<th></th>
				</tr>
			</thead>
			<tbody>
				{data.map((b) => (
					<tr key={b.id} style={showStatus ? { opacity: 0.6 } : {}}>
						<td>{b.travelerName}</td>
						<td>{b.travelerEmail}</td>
						<td>{b.travelerPhone}</td>
						<td>{b.scheduledCheckInDate?.slice(0, 10)}</td>
						<td>{b.stationName}</td>
						{showStatus && <td>✅ Checked-In</td>}
						<td>
							<Button
								variant={showStatus ? 'light' : 'filled'}
								size='xs'
								onClick={() => handleSelect(b)}
							>
								{showStatus ? 'View' : 'Select'}
							</Button>
						</td>
					</tr>
				))}
			</tbody>
		</Table>
	)

	return (
		<Box px='md' py='md'>
			<Title order={2} mb='md'>
				Check-in Travelers
			</Title>

			{currentExperience?.status === 'inactive' ? (
				'This experience is currently inactive.'
			) : (
				<>
					{!scanning && (
						<form onSubmit={handleSearch}>
							<Group align='flex-end' spacing='xs' noWrap>
								<TextInput
									label='Search'
									placeholder='Name, Email, Booking Number, or Phone'
									value={query}
									onChange={handleInputChange}
									w='100%'
								/>
								<Button type='submit' disabled={query.trim().length < 3}>
									Search
								</Button>
								<Button variant='light' onClick={() => setScanning(true)}>
									Scan QR
								</Button>
							</Group>
						</form>
					)}

					{scanning && (
						<CameraScanner
							onScanSuccess={handleScanSuccess}
							onScanFailure={(err) => console.error('Scan error:', err)}
							onClose={() => setScanning(false)}
						/>
					)}

					{!scanning && error && (
						<Text color='red' mt='sm'>
							{error}
						</Text>
					)}

					<Space h='md' />

					{!scanning && hasSearched && checkIns.length === 0 && !error && (
						<Text>No results found.</Text>
					)}

					{!scanning && checkIns.length > 0 && (
						<>
							<Title order={4}>My Station: {userStationName}</Title>

							<MediaQuery largerThan='sm' styles={{ display: 'none' }}>
								<Box>{renderCards(myStationCheckIns)}</Box>
							</MediaQuery>
							<MediaQuery smallerThan='sm' styles={{ display: 'none' }}>
								<Box>
									<Paper withBorder p='sm' mt='xs'>
										{renderTable(myStationCheckIns)}
									</Paper>
								</Box>
							</MediaQuery>

							{otherStationCheckIns.length > 0 && (
								<>
									<Title order={5} mt='lg'>
										Other Check-in Stations
									</Title>
									<MediaQuery largerThan='sm' styles={{ display: 'none' }}>
										<Box>{renderCards(otherStationCheckIns)}</Box>
									</MediaQuery>
									<MediaQuery smallerThan='sm' styles={{ display: 'none' }}>
										<Box>
											<Paper withBorder p='sm' mt='xs'>
												{renderTable(otherStationCheckIns)}
											</Paper>
										</Box>
									</MediaQuery>
								</>
							)}

							{alreadyCheckedIn.length > 0 && (
								<>
									<Title order={5} mt='lg'>
										Already Checked-In
									</Title>
									<MediaQuery largerThan='sm' styles={{ display: 'none' }}>
										<Box>{renderCards(alreadyCheckedIn, '✅ Checked-In')}</Box>
									</MediaQuery>
									<MediaQuery smallerThan='sm' styles={{ display: 'none' }}>
										<Box>
											<Paper withBorder p='sm' mt='xs'>
												{renderTable(alreadyCheckedIn, true)}
											</Paper>
										</Box>
									</MediaQuery>
								</>
							)}
						</>
					)}
				</>
			)}
		</Box>
	)
}

export default ExperienceSearchPage
